import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useSearchParams, Link } from 'react-router-dom';

import Logo from '../components/ui/Logo';
import Btn from '../components/ui/Btn';
import { Avatar, Badge, Card, Spinner } from '../components/ui/primitives';
import { Icon } from '../components/ui/Icon';
import { ErrorBanner } from './auth/SignInScreen';
import SmsOptInWelcome from '../components/portal/SmsOptInWelcome';
import { supabase } from '../lib/supabase';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { useTheme } from '../lib/theme';
import { useToast } from '../lib/toast';
import { useIsMobile } from '../lib/useBreakpoint';

/**
 * PortalScreen — the client-facing upload view.
 *
 * Lifecycle:
 *   1. Read team_id from `?team=` URL param
 *   2. Load: team info, current user's membership/role for this team,
 *      template_role_assignments for that role, the user's existing uploads
 *   3. Render branded header + progress card + upload list
 *   4. Each row: drag/drop zone or file picker → upload to Supabase Storage
 *      → call /api/classify-upload → insert document_uploads row
 *   5. Show status (in_review / approved / rejected) + replace flow
 */
export default function PortalScreen() {
  const [params] = useSearchParams();
  const teamId = params.get('team');
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const toast = useToast();
  const { theme, toggle: toggleTheme } = useTheme();
  const isMobile = useIsMobile();

  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState(null);
  const [team, setTeam]             = useState(null);
  const [membership, setMembership] = useState(null); // { id, team_roles: { ... } }
  const [templates, setTemplates]   = useState([]);   // assigned to this user's role
  const [uploads, setUploads]       = useState([]);   // by this user, by template_id

  // SMS opt-in welcome modal dismissal — session-local. The backend already
  // flips sms_opt_in_completed=true on submit (either Continue or Skip), so
  // this state is just a guard against the modal flickering back during the
  // same session before `membership` is refetched.
  const [optInDismissed, setOptInDismissed] = useState(false);

  // ── Initial load ──
  useEffect(() => {
    if (!user || !teamId) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        // Team
        const { data: teamRow, error: tErr } = await supabase
          .from('teams')
          .select('id, name, slug, primary_color, logo_url, language_default')
          .eq('id', teamId)
          .single();
        if (tErr) throw tErr;

        // Membership
        const { data: memRow, error: mErr } = await supabase
          .from('team_members')
          .select('id, role_id, first_name, last_name, language, sms_opt_in_completed, phone_number, team_roles ( id, name, slug, is_staff )')
          .eq('team_id', teamId)
          .eq('user_id', user.id)
          .maybeSingle();
        if (mErr) throw mErr;
        if (!memRow) throw new Error('You are not a member of this workspace.');

        // Templates assigned to this role
        const { data: assignRows, error: aErr } = await supabase
          .from('template_role_assignments')
          .select('template_id, document_templates ( id, label, label_es, key, category, required, has_expiration, sort_order )')
          .eq('team_id', teamId)
          .eq('role_id', memRow.role_id);
        if (aErr) throw aErr;

        const tpls = (assignRows || [])
          .map(a => a.document_templates)
          .filter(Boolean)
          .sort((a, b) => (a.sort_order || 0) - (b.sort_order || 0) || a.label.localeCompare(b.label));

        // Existing uploads by this member
        const { data: uploadRows, error: uErr } = await supabase
          .from('document_uploads')
          .select('id, template_id, storage_path, original_filename, display_name, status, admin_notes, expires_at, created_at')
          .eq('team_id', teamId)
          .eq('member_id', memRow.id)
          .order('created_at', { ascending: false });
        if (uErr) throw uErr;

        if (cancelled) return;
        setTeam(teamRow);
        setMembership(memRow);
        setTemplates(tpls);
        setUploads(uploadRows || []);
      } catch (err) {
        console.error('Portal load failed:', err);
        if (!cancelled) setError(err.message || 'Failed to load portal.');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [user, teamId]);

  // ── Derived: latest upload per template ──
  const uploadsByTemplate = useMemo(() => {
    const map = {};
    // Sorted desc by created_at, so first occurrence is latest
    uploads.forEach(u => {
      if (!map[u.template_id]) map[u.template_id] = u;
    });
    return map;
  }, [uploads]);

  // ── Derived: unmatched uploads (no template assigned). Client can classify these. ──
  const unmatchedUploads = useMemo(
    () => uploads.filter(u => !u.template_id && u.status !== '__uploading__'),
    [uploads]
  );

  const requiredTemplates = templates.filter(t => t.required);
  const completed = requiredTemplates.filter(t => {
    const u = uploadsByTemplate[t.id];
    return u && (u.status === 'approved' || u.status === 'in_review');
  }).length;
  const totalRequired = requiredTemplates.length;

  // ── Upload + classify flow ──
  const startUpload = async (template, file) => {
    if (!file) return;
    if (file.size > 25 * 1024 * 1024) {
      toast.error('File too large — max 25 MB per upload.');
      return;
    }

    // Optimistically mark this template as "uploading" (but this row gets replaced
    // when the real DB row is inserted). We use a temp id prefixed with __tmp__
    const tmpId = '__tmp__' + Date.now();
    const tmpRow = {
      id: tmpId,
      template_id: template.id,
      original_filename: file.name,
      status: '__uploading__',
      created_at: new Date().toISOString(),
    };
    setUploads([tmpRow, ...uploads.filter(u => u.template_id !== template.id || u.status !== '__uploading__')]);

    try {
      // 1. Upload file to Supabase storage
      const ext = (file.name.split('.').pop() || 'bin').toLowerCase();
      const safeKey = (template.key || template.id).replace(/[^a-z0-9_-]/gi, '_');
      const path = `${teamId}/${membership.id}/${safeKey}_${Date.now()}.${ext}`;

      const { error: upErr } = await supabase.storage
        .from('client-docs')
        .upload(path, file, { upsert: false, contentType: file.type || undefined });
      if (upErr) throw new Error('Upload failed: ' + upErr.message);

      // 2. Insert document_uploads row in 'in_review' status — this is the source of truth.
      //    The column is `storage_path` (relative key), not a full URL.
      //    Signed URLs are generated on demand when staff opens the file viewer.
      const { data: inserted, error: insErr } = await supabase
        .from('document_uploads')
        .insert({
          team_id: teamId,
          member_id: membership.id,
          template_id: template.id,
          storage_path: path,
          original_filename: file.name,
          display_name: template.label,
          status: 'in_review',
        })
        .select()
        .single();
      if (insErr) throw insErr;

      // Replace the optimistic temp row with the real DB row
      setUploads(prev => [inserted, ...prev.filter(u => u.id !== tmpId)]);

      // 3. Best-effort: ping the AI classifier in the background.
      // Throttled to share the global classify concurrency limit (max 2 in
      // flight, 250ms stagger) — defensive guard against backend token-limit
      // errors when many uploads happen in rapid succession.
      api.classifyUploadThrottled({
        team_id: teamId,
        upload_id: inserted.id,
        storage_path: path,
        suggested_template_id: template.id,
      }).catch(err => {
        console.warn('AI classification failed (upload still saved):', err);
      });
    } catch (err) {
      console.error('Upload failed:', err);
      toast.error('Upload failed: ' + (err.message || 'unknown error'));
      // Remove the optimistic row
      setUploads(prev => prev.filter(u => u.id !== tmpId));
    }
  };

  // ── Client self-classification: assign a template to one of their unmatched docs ──
  const classifyMyUpload = async (uploadId, templateId) => {
    const tpl = templates.find(t => t.id === templateId);
    if (!tpl) return;
    try {
      const { data, error: updErr } = await supabase
        .from('document_uploads')
        .update({
          template_id: templateId,
          display_name: tpl.label,
          // Reset status to in_review so staff can verify the new classification
          status: 'in_review',
        })
        .eq('id', uploadId)
        .select()
        .single();
      if (updErr) throw updErr;
      setUploads(prev => prev.map(u => u.id === uploadId ? data : u));
    } catch (err) {
      console.error('Self-classify failed:', err);
      toast.error('Could not classify: ' + (err.message || 'unknown error'));
    }
  };

  // ── Render: error / loading ──
  if (loading) {
    return (
      <div style={{
        minHeight: '100vh', background: 'var(--bg)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        color: 'var(--text-3)', gap: 10, fontSize: 13,
      }}>
        <Spinner size={18} color="var(--accent)" />
        <span>Loading your portal…</span>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{ background: 'var(--bg)', minHeight: '100vh', padding: '60px 24px' }}>
        <Card style={{ maxWidth: 480, margin: '0 auto', padding: 28, textAlign: 'center' }}>
          <div style={{
            width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
            background: 'var(--danger-bg)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
          }}>
            <Icon.alert size={26} stroke="var(--danger)" />
          </div>
          <h1 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Can't open this portal</h1>
          <p style={{ fontSize: 13.5, color: 'var(--text-3)', marginBottom: 20 }}>{error}</p>
          <Link to="/hub" style={{ textDecoration: 'none' }}>
            <Btn variant="primary" size="md" icon={Icon.home}>Back to Hub</Btn>
          </Link>
        </Card>
      </div>
    );
  }

  if (!teamId) {
    return (
      <div style={{ background: 'var(--bg)', minHeight: '100vh', padding: '60px 24px' }}>
        <Card style={{ maxWidth: 480, margin: '0 auto', padding: 28, textAlign: 'center' }}>
          <h1 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>Pick a workspace</h1>
          <p style={{ fontSize: 13.5, color: 'var(--text-3)', marginBottom: 20 }}>
            The portal needs a workspace. Open a client workspace from your Hub.
          </p>
          <Link to="/hub" style={{ textDecoration: 'none' }}>
            <Btn variant="primary" size="md" icon={Icon.home}>Go to Hub</Btn>
          </Link>
        </Card>
      </div>
    );
  }

  // ── Render: main ──
  const firstName = membership?.first_name || user?.user_metadata?.first_name || (user?.email || '').split('@')[0];

  // SMS opt-in welcome — show once per recipient. Strict `=== false` check
  // means older rows where the column is missing/undefined are treated as
  // "already prompted, don't show again", per spec. We don't gate the rest
  // of the portal behind this: the modal overlays the live portal so the
  // user can dismiss and proceed (or skip) at any time.
  const showSmsOptIn =
    !!membership &&
    membership.sms_opt_in_completed === false &&
    !optInDismissed;
  const teamColor = team?.primary_color || '#0a0f2e';
  const teamInitials = (team?.name || '?').split(/\s+/).map(s => s[0]).slice(0, 2).join('').toUpperCase();
  const userFull = [membership?.first_name, membership?.last_name].filter(Boolean).join(' ') || user?.email || 'You';
  const userShort = membership?.first_name
    ? `${membership.first_name} ${(membership.last_name || '').charAt(0)}${membership.last_name ? '.' : ''}`.trim()
    : userFull;

  return (
    <div style={{ background: 'var(--bg)', minHeight: '100vh' }}>
      {/* One-time SMS opt-in welcome (overlays the portal — does NOT gate it) */}
      {showSmsOptIn && (
        <SmsOptInWelcome
          teamMember={{ id: membership.id, first_name: membership.first_name }}
          onComplete={() => setOptInDismissed(true)}
        />
      )}

      {/* Branded header */}
      <div style={{
        background: teamColor, color: '#fff',
        padding: isMobile ? '12px 16px' : '14px 32px',
        display: 'flex', alignItems: 'center',
        gap: isMobile ? 8 : 12,
      }}>
        <div style={{
          width: 32, height: 32, borderRadius: 8,
          background: team?.logo_url ? `url(${team.logo_url}) center / cover` : '#3dc75b',
          color: '#0a0f2e', display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 13, flexShrink: 0,
        }}>
          {!team?.logo_url && teamInitials}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{
            fontSize: 13.5, fontWeight: 700,
            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          }}>
            {team?.name || 'Workspace'}{isMobile ? '' : ' · Onboarding'}
          </div>
          {!isMobile && (
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.6)' }}>
              Secure intake powered by MagicIntake
            </div>
          )}
        </div>
        <Btn
          variant="ghost"
          size="sm"
          icon={theme === 'dark' ? Icon.sun : Icon.moon}
          onClick={toggleTheme}
          style={{ color: '#fff' }}
          title="Toggle theme"
        />
        {!isMobile && (
          <>
            <div style={{ width: 1, height: 20, background: 'rgba(255,255,255,0.15)' }} />
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <Avatar name={userFull} size={28} color="#3b82f6" />
              <span style={{ fontSize: 12.5, fontWeight: 500 }}>{userShort}</span>
            </div>
          </>
        )}
        {isMobile && <Avatar name={userFull} size={28} color="#3b82f6" />}
        <Btn
          variant="ghost"
          size="sm"
          icon={Icon.logout}
          onClick={async () => { await signOut(); navigate('/sign-in', { replace: true }); }}
          style={{ color: 'rgba(255,255,255,0.7)' }}
          title="Sign out"
        />
      </div>

      <div style={{
        maxWidth: 960, margin: '0 auto',
        padding: isMobile ? '24px 16px 48px' : '40px 32px 60px',
      }}>
        {/* Hero */}
        <div style={{ marginBottom: isMobile ? 20 : 28 }}>
          <Badge variant="accent">{membership?.team_roles?.name || 'Client'} portal</Badge>
          <h1 style={{
            fontSize: isMobile ? 24 : 30, fontWeight: 700, letterSpacing: '-0.025em',
            marginTop: 10, marginBottom: 6,
          }}>
            Welcome, {firstName} — let's get you set up
          </h1>
          <p style={{ fontSize: 14, color: 'var(--text-3)', maxWidth: 560 }}>
            {team?.name || 'Your team'} needs {totalRequired === 0
              ? 'no required documents at the moment.'
              : `${totalRequired} document${totalRequired === 1 ? '' : 's'} from you.`}{' '}
            {templates.length > 0 && 'Upload each below — our AI verifies format and routes to a reviewer automatically.'}
          </p>
        </div>

        {templates.length === 0 ? (
          /* No requirements */
          <Card style={{ padding: 40, textAlign: 'center' }}>
            <div style={{
              width: 56, height: 56, borderRadius: 16, margin: '0 auto 16px',
              background: 'var(--accent-bg)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}>
              <Icon.check size={26} stroke="var(--accent-600)" strokeWidth={2.5} />
            </div>
            <h2 style={{ fontSize: 18, fontWeight: 700, marginBottom: 8 }}>
              Nothing to upload right now
            </h2>
            <p style={{ fontSize: 13.5, color: 'var(--text-3)' }}>
              {team?.name || 'Your team'} hasn't assigned any documents to your role yet.
              You'll be notified when something needs your attention.
            </p>
          </Card>
        ) : (
          <>
            {/* Progress card — only show if there are required docs */}
            {totalRequired > 0 && (
              <Card style={{
                padding: isMobile ? 16 : 20, marginBottom: 20,
                background: 'linear-gradient(135deg, var(--accent-bg), var(--surface))',
              }}>
                <div style={{
                  display: 'flex',
                  flexDirection: isMobile ? 'column' : 'row',
                  alignItems: isMobile ? 'center' : 'center',
                  textAlign: isMobile ? 'center' : 'left',
                  gap: isMobile ? 14 : 20, flexWrap: 'wrap',
                }}>
                  <ProgressRing value={completed} total={totalRequired} size={isMobile ? 64 : 72} />
                  <div style={{ flex: 1, minWidth: isMobile ? 0 : 180 }}>
                    <div style={{ fontSize: 15, fontWeight: 700, marginBottom: 2 }}>
                      You're {completed} of {totalRequired} done
                    </div>
                    <div style={{ fontSize: 12.5, color: 'var(--text-2)' }}>
                      {totalRequired - completed > 0 ? (
                        <>About <strong>{(totalRequired - completed) * 2} minutes</strong> left. We save your progress automatically.</>
                      ) : 'All required documents submitted. Reviewers will follow up if anything needs changes.'}
                    </div>
                  </div>
                </div>
              </Card>
            )}

            {/* Doc list */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {templates.map((tpl, i) => (
                <PortalReqCard
                  key={tpl.id}
                  tpl={tpl}
                  idx={i + 1}
                  language={membership?.language || team?.language_default || 'en'}
                  upload={uploadsByTemplate[tpl.id]}
                  onUpload={(file) => startUpload(tpl, file)}
                />
              ))}
            </div>
          </>
        )}

        {/* Bottom resources */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: isMobile
            ? '1fr'
            : 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: isMobile ? 10 : 14, marginTop: 28,
        }}>
          <Card style={{ padding: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'var(--info-bg)', color: 'var(--info)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon.shield size={18} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Your data is encrypted</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                SOC 2 · HIPAA · 256-bit at rest
              </div>
            </div>
          </Card>
          <Card style={{ padding: 18, display: 'flex', alignItems: 'center', gap: 14 }}>
            <div style={{
              width: 40, height: 40, borderRadius: 10,
              background: 'var(--accent-bg)', color: 'var(--accent-600)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              flexShrink: 0,
            }}>
              <Icon.help size={18} />
            </div>
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>Need help?</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                Contact your workspace admin
              </div>
            </div>
          </Card>
        </div>

        {/* Powered by footer */}
        <div style={{
          textAlign: 'center', marginTop: 32,
          fontSize: 11, color: 'var(--text-muted)',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
        }}>
          Powered by
          <Logo size={22} useFile />
        </div>
      </div>
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Single requirement card
// ────────────────────────────────────────────────────────────────────────────
function PortalReqCard({ tpl, idx, language, upload, onUpload }) {
  const isMobile = useIsMobile();
  const [drag, setDrag] = useState(false);

  const isUploading = upload?.status === '__uploading__';
  const isApproved  = upload?.status === 'approved';
  const isRejected  = upload?.status === 'rejected';
  const isReview    = upload?.status === 'in_review';
  const isDone      = isApproved || isReview;

  const label = (language === 'es' && tpl.label_es) ? tpl.label_es : tpl.label;

  const statusPill = isApproved  ? <Badge variant="ok"     dot>Approved</Badge>
                  : isReview     ? <Badge variant="warn"   dot>In review</Badge>
                  : isRejected   ? <Badge variant="danger" dot>Needs resubmit</Badge>
                  : isUploading  ? <Badge variant="info"   dot>Uploading…</Badge>
                  : tpl.required ? <Badge variant="neutral">Required</Badge>
                                 : <Badge variant="neutral">Optional</Badge>;

  const expiresInfo = (() => {
    if (!isApproved || !upload?.expires_at) return null;
    const days = Math.ceil((new Date(upload.expires_at).getTime() - Date.now()) / 86400000);
    if (days < 0)  return { color: 'var(--danger)', label: `Expired ${-days}d ago` };
    if (days < 30) return { color: 'var(--warn)',   label: `Expires in ${days}d` };
    return null;
  })();

  // ── Drag handlers ──
  const onDrop = (e) => {
    e.preventDefault();
    setDrag(false);
    if (isUploading) return;
    const file = e.dataTransfer.files?.[0];
    if (file) onUpload(file);
  };

  const onPickFile = (e) => {
    const file = e.target.files?.[0];
    if (file) onUpload(file);
    e.target.value = ''; // reset so the same file can be re-picked
  };

  return (
    <Card
      style={{
        padding: 0, overflow: 'hidden',
        borderColor: isApproved ? 'rgba(61,199,91,0.3)' :
                     isRejected ? 'rgba(239,68,68,0.3)' :
                     drag       ? 'var(--accent)'      :
                                  'var(--border)',
        boxShadow: drag ? 'var(--shadow-glow)' : 'var(--shadow-xs)',
        transition: 'border-color 0.15s, box-shadow 0.15s',
      }}
      onDragOver={(e) => { e.preventDefault(); if (!isUploading) setDrag(true); }}
      onDragLeave={() => setDrag(false)}
      onDrop={onDrop}
    >
      <div style={{ padding: 16, display: 'flex', alignItems: 'center', gap: 14, flexWrap: 'wrap' }}>
        <div style={{
          width: 40, height: 40, borderRadius: 12, flexShrink: 0,
          background: isDone ? 'var(--accent)' : isRejected ? 'var(--danger-bg)' : 'var(--surface-2)',
          color:      isDone ? '#fff'         : isRejected ? 'var(--danger)'     : 'var(--text-3)',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontWeight: 700, fontSize: 14,
        }}>
          {isDone     ? <Icon.check size={18} stroke="#fff"           strokeWidth={2.5} />
         : isRejected ? <Icon.alert size={18} stroke="var(--danger)" />
         :              idx}
        </div>

        <div style={{ flex: 1, minWidth: 200 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8, marginBottom: 2, flexWrap: 'wrap',
          }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: 'var(--text)' }}>{label}</div>
            {statusPill}
            {expiresInfo && (
              <span style={{ fontSize: 11, color: expiresInfo.color, fontWeight: 600 }}>
                {expiresInfo.label}
              </span>
            )}
          </div>
          <div style={{ fontSize: 12, color: 'var(--text-3)' }}>
            {tpl.has_expiration && <>Tracked for expiration · </>}
            {upload?.original_filename
              ? <>File: <span style={{ fontFamily: 'var(--font-mono)' }}>{upload.original_filename}</span></>
              : drag ? 'Drop file to upload' : 'Drag & drop here, or click "Upload"'}
          </div>
        </div>

        {/* Right-side actions (desktop) or full-width row (mobile) */}
        {!isDone && !isUploading && (
          <label style={isMobile ? { width: '100%' } : undefined}>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.heic,.webp,.doc,.docx"
              onChange={onPickFile}
              style={{ display: 'none' }}
            />
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-start',
              gap: 6,
              padding: isMobile ? '12px 18px' : '8px 14px',
              height: isMobile ? 44 : 36,
              width: isMobile ? '100%' : undefined,
              background: 'var(--accent)', color: '#fff',
              border: 'none', borderRadius: 8,
              fontSize: isMobile ? 14 : 13, fontWeight: 600, cursor: 'pointer',
              boxShadow: '0 1px 0 rgba(0,0,0,0.04), 0 4px 12px rgba(61,199,91,0.24)',
              fontFamily: 'inherit', boxSizing: 'border-box',
            }}>
              <Icon.upload size={isMobile ? 17 : 15} />
              {isRejected ? 'Resubmit' : 'Upload'}
            </span>
          </label>
        )}
        {isApproved && (
          <label style={isMobile ? { width: '100%' } : undefined}>
            <input
              type="file"
              accept=".pdf,.png,.jpg,.jpeg,.heic,.webp,.doc,.docx"
              onChange={onPickFile}
              style={{ display: 'none' }}
            />
            <span style={{
              display: 'inline-flex', alignItems: 'center',
              justifyContent: isMobile ? 'center' : 'flex-start',
              gap: 6,
              padding: isMobile ? '10px 14px' : '6px 10px',
              height: isMobile ? 44 : 30,
              width: isMobile ? '100%' : undefined,
              fontSize: isMobile ? 13 : 12,
              background: isMobile ? 'var(--surface-2)' : 'transparent',
              color: 'var(--text-2)',
              border: isMobile ? '1px solid var(--border)' : 'none',
              cursor: 'pointer', fontFamily: 'inherit',
              borderRadius: 8, boxSizing: 'border-box',
            }}>
              <Icon.refresh size={isMobile ? 14 : 12} />
              Replace
            </span>
          </label>
        )}
        {isReview && (
          <Btn variant="ghost" size="sm" disabled style={isMobile ? { width: '100%', justifyContent: 'center' } : undefined}>
            Awaiting review
          </Btn>
        )}
      </div>

      {/* Uploading progress strip */}
      {isUploading && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{
            padding: 14,
            background: 'var(--info-bg)',
            border: '1px solid rgba(59,130,246,0.25)',
            borderRadius: 10,
            display: 'flex', alignItems: 'center', gap: 12,
          }}>
            <div className="mi-pulse" style={{
              width: 10, height: 10, borderRadius: '50%',
              background: 'var(--info)', flexShrink: 0,
            }} />
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, marginBottom: 4, color: 'var(--text)' }}>
                Uploading & analyzing your file…
              </div>
              <div style={{
                height: 4, background: 'rgba(59,130,246,0.2)',
                borderRadius: 2, overflow: 'hidden',
              }}>
                <div className="mi-progress" style={{
                  height: '100%', background: 'var(--info)', borderRadius: 2,
                }} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rejection reason banner */}
      {isRejected && upload?.admin_notes && (
        <div style={{ padding: '0 16px 16px' }}>
          <div style={{
            padding: 12,
            background: 'var(--danger-bg)',
            border: '1px solid rgba(239,68,68,0.22)',
            borderRadius: 8,
            fontSize: 12.5,
            color: 'var(--text-2)',
            lineHeight: 1.5,
          }}>
            <strong style={{ color: 'var(--danger)' }}>Reason:</strong>{' '}
            {upload.admin_notes}
          </div>
        </div>
      )}
    </Card>
  );
}

// ────────────────────────────────────────────────────────────────────────────
// Tiny progress ring for the portal hero
// ────────────────────────────────────────────────────────────────────────────
function ProgressRing({ value, total, size = 72 }) {
  const r = size / 2 - 6;
  const c = 2 * Math.PI * r;
  const frac = total === 0 ? 0 : Math.max(0, Math.min(1, value / total));
  return (
    <div style={{ position: 'relative', width: size, height: size, flexShrink: 0 }}>
      <svg width={size} height={size} style={{ transform: 'rotate(-90deg)' }}>
        <circle cx={size/2} cy={size/2} r={r} stroke="var(--border)" strokeWidth="6" fill="none" />
        <circle
          cx={size/2} cy={size/2} r={r}
          stroke="var(--accent)" strokeWidth="6" fill="none"
          strokeLinecap="round"
          strokeDasharray={c} strokeDashoffset={c * (1 - frac)}
          style={{ transition: 'stroke-dashoffset 0.4s ease' }}
        />
      </svg>
      <div style={{
        position: 'absolute', inset: 0,
        display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: 'var(--text)' }}>
          {value}/{total}
        </div>
      </div>
    </div>
  );
}
